import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { productsAPI } from '../services/api';

const Home = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    try {
      const products = await productsAPI.getAll(null, true);
      setFeaturedProducts(products.slice(0, 4));
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-90" />
        
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-4xl">
            <h1 className="text-6xl md:text-8xl lg:text-[12rem] font-black tracking-tighter leading-[0.85] mb-8">
              BORN
              <br />
              FROM
              <br />
              SHINE
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-12 max-w-md">
              Where audacity meets elegance. Contemporary fashion without compromise.
            </p>
            <button
              onClick={() => navigate('/catalog')}
              className="group bg-white text-black px-10 py-5 text-sm font-bold tracking-wider hover:bg-gray-200 transition-colors inline-flex items-center gap-3"
            >
              EXPLORE COLLECTION
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} strokeWidth={3} />
            </button>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute top-20 right-20 w-64 h-64 bg-white/3 rounded-full blur-3xl" />
      </section>

      {/* Featured Products */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-end mb-16">
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter">
              FEATURED
            </h2>
            <button
              onClick={() => navigate('/catalog')}
              className="text-sm font-bold tracking-wider hover:opacity-50 transition-opacity inline-flex items-center gap-2"
            >
              VIEW ALL
              <ArrowRight size={16} strokeWidth={3} />
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-2xl font-bold tracking-wider text-gray-400">LOADING...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              {featuredProducts.map(product => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Brand Philosophy - Asymmetric Layout */}
      <section className="py-24 lg:py-32 bg-black text-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="space-y-8">
              <h2 className="text-5xl md:text-7xl font-black tracking-tighter leading-tight">
                UNCOMPROMISING
                <br />
                VISION
              </h2>
              <div className="space-y-6 text-gray-300">
                <p className="text-lg leading-relaxed">
                  Born from the intersection of raw creativity and refined execution, we challenge conventional aesthetics.
                </p>
                <p className="text-lg leading-relaxed">
                  Each piece is a statement—architectural, bold, and unapologetically modern.
                </p>
              </div>
            </div>

            <div className="aspect-square bg-gradient-to-br from-gray-900 to-black relative overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-9xl font-black opacity-10 tracking-tighter">BFS</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter mb-16">
            CATEGORIES
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Outerwear */}
            <div
              onClick={() => navigate('/catalog?category=outerwear')}
              className="group cursor-pointer aspect-[4/3] bg-black text-white relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-black to-gray-900 group-hover:scale-105 transition-transform duration-700" />
              <div className="relative h-full flex items-end p-8 lg:p-12">
                <h3 className="text-4xl lg:text-6xl font-black tracking-tighter">OUTERWEAR</h3>
              </div>
            </div>

            {/* Tops */}
            <div
              onClick={() => navigate('/catalog?category=tops')}
              className="group cursor-pointer aspect-[4/3] bg-gray-100 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 group-hover:scale-105 transition-transform duration-700" />
              <div className="relative h-full flex items-end p-8 lg:p-12">
                <h3 className="text-4xl lg:text-6xl font-black tracking-tighter">TOPS</h3>
              </div>
            </div>

            {/* Bottoms */}
            <div
              onClick={() => navigate('/catalog?category=bottoms')}
              className="group cursor-pointer aspect-[4/3] bg-gray-200 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 group-hover:scale-105 transition-transform duration-700" />
              <div className="relative h-full flex items-end p-8 lg:p-12">
                <h3 className="text-4xl lg:text-6xl font-black tracking-tighter">BOTTOMS</h3>
              </div>
            </div>

            {/* Accessories */}
            <div
              onClick={() => navigate('/catalog?category=accessories')}
              className="group cursor-pointer aspect-[4/3] bg-black text-white relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-black group-hover:scale-105 transition-transform duration-700" />
              <div className="relative h-full flex items-end p-8 lg:p-12">
                <h3 className="text-4xl lg:text-6xl font-black tracking-tighter">ACCESSORIES</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-black text-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-8">
            JOIN THE MOVEMENT
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
            Subscribe for exclusive early access to new drops and limited editions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 bg-white text-black px-6 py-4 text-sm focus:outline-none focus:ring-2 focus:ring-white"
            />
            <button className="bg-white text-black px-8 py-4 text-sm font-bold tracking-wider hover:bg-gray-200 transition-colors">
              SUBSCRIBE
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
